import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../user-dashboard/MainLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  supabase,
  savePracticeLog,
  getUserPracticeLog,
  fetchUserBadges,
  awardBadge,
  initializeUserSessions
} from '../../supabase';

// Updated badge definitions - one badge per session week for daily practice
const badgeDefinitions = {
  // Session week badges - earned by practicing at least once every day during that session's week
  "Session 1 Pain Management Practice Badge": {
    description: "Practice every day during Session 1 week",
    color: "#EFF6FF",
    requiredDays: 7,
    sessionNumber: 1,
    type: "session_week"
  },
  "Session 2 Pain Management Practice Badge": {
    description: "Practice every day during Session 2 week",
    color: "#F0F9FF",
    requiredDays: 7,
    sessionNumber: 2,
    type: "session_week"
  },
  "Session 3 Pain Management Practice Badge": {
    description: "Practice every day during Session 3 week",
    color: "#F8F9FA",
    requiredDays: 7,
    sessionNumber: 3,
    type: "session_week"
  },
  "Session 4 Pain Management Practice Badge": {
    description: "Practice every day during Session 4 week",
    color: "#ECFDF5",
    requiredDays: 7,
    sessionNumber: 4,
    type: "session_week"
  },
  "Session 5 Pain Management Practice Badge": {
    description: "Practice every day during Session 5 week",
    color: "#EFF6FF",
    requiredDays: 7,
    sessionNumber: 5,
    type: "session_week"
  },
  
  // Milestone badges
  "First Step": {
    description: "Log your first practice session",
    color: "#EFF6FF",
    requiredCount: 1,
    type: "milestone"
  },
  "Building Momentum": {
    description: "Complete 10 practice sessions total",
    color: "#F0F9FF",
    requiredCount: 10,
    type: "milestone"
  },
  "Committed Practitioner": {
    description: "Complete 25 practice sessions total",
    color: "#F8F9FA",
    requiredCount: 25,
    type: "milestone"
  }
};


const ModernPracticeLog = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  // UI State
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const location = useLocation();


  // Practice data state
  const [practiceData, setPracticeData] = useState({});
  const [badges, setBadges] = useState([]);
  const [badgesLoaded, setBadgesLoaded] = useState(false);
  const [skillCounts, setSkillCounts] = useState({});
  const [totalPractices, setTotalPractices] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);

  // Session data state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);

  // Modal and form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPracticeId, setEditingPracticeId] = useState(null);
  const [skill, setSkill] = useState('');
  const [minutes, setMinutes] = useState('');
  const [practiceType, setPracticeType] = useState('self-guided');

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Badge state
  const [newBadge, setNewBadge] = useState(null);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);

  // History state
  const [historyPage, setHistoryPage] = useState(0);
  const [historySort, setHistorySort] = useState('date');
  const ITEMS_PER_PAGE = 3;
  const [recentActivityCollapsed, setRecentActivityCollapsed] = useState(true);

  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');

  // Weekly stats
  const [weeklyStats, setWeeklyStats] = useState({
    totalMinutes: 0,
    practiceCount: 0,
    topSkill: '',
    completionPercentage: 0,
    dailyPractice: {}
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

const fetchUserSessions = useCallback(async () => {
  if (!user?.id) return;
  
  try {
    // Initialize user sessions first
    await initializeUserSessions(user.id);

    // Fetch sessions with progress data
    const { data: sessionsData, error } = await supabase
      .from('user_sessions')
      .select(
        `
        session_number,
        session_completed,
        session_fully_completed,
        session_started,
        video_watched,
        video_completed,
        quiz_completed,
        created_at,
        pre_assessment_completed,
        weekly_goals
      `,
      )
      .eq('user_id', user.id)
      .order('session_number');

    if (error) {
      console.error('Error fetching sessions:', error);
      setSessionsLoaded(true);
      return;
    }

    if (!sessionsData || sessionsData.length === 0) {
      setSessions([]);
      setSessionsLoaded(true);
      return;
    }

    // Fetch user's session schedule to check unlock times
    const { data: schedule } = await supabase
      .from('participant_session_schedule')
      .select('*')
      .eq('user_id', user.id)
      .single();

    console.log('📅 Practice Log - User session schedule:', schedule);

    // Fetch manual overrides
    const { data: manualOverrides } = await supabase
      .from('manual_session_overrides')
      .select('*')
      .eq('user_id', user.id);

    console.log('🔓 Practice Log - Manual session overrides:', manualOverrides);

    // Session metadata - same as dashboard
    const sessionMetadata = [
      {
        id: 'session0',
        number: 0,
        title: 'Welcome & Program Introduction',
        subtitle: 'Get oriented with the program and set expectations.',
        estimatedTime: '15 min',
      },
      {
        id: 'session1',
        number: 1,
        title: 'Taking Charge of Orofacial Pain',
        subtitle:
          'Understand chronic pain and practice deep breathing techniques.',
        estimatedTime: '20 min',
      },
      {
        id: 'session2',
        number: 2,
        title: 'Managing Emotional Challenges in Orofacial Pain',
        subtitle:
          'Break negative pain spirals with mindfulness and body scan practice.',
        estimatedTime: '23 min',
      },
      {
        id: 'session3',
        number: 3,
        title: 'Working with Negative Thoughts in Orofacial Pain',
        subtitle:
          'Challenge negative thoughts using catch, check, and change techniques.',
        estimatedTime: '19 min',
      },
      {
        id: 'session4',
        number: 4,
        title: 'Not Letting Orofacial Pain Get the Best of Us',
        subtitle:
          'Practice acceptance and re-engage in meaningful life activities.',
        estimatedTime: '22 min',
      },
      {
        id: 'session5',
        number: 5,
        title:
          'Facing Forward: Rolling with Orofacial Pain & Staying Resilient',
        subtitle:
          'Maintain progress and build long-term resilience strategies.',
        estimatedTime: '22 min',
      },
    ];

    // FIXED: Transform sessions with EXACT SAME logic as Dashboard
    const transformedSessions = sessionMetadata.map((metadata) => {
      const sessionData = sessionsData.find(
        (s) => s.session_number === metadata.number,
      );

      // Check for manual override for this session
      const override = manualOverrides?.find(
        (o) => o.session_number === metadata.number,
      );

      // Determine if session is unlocked
      let isUnlocked = metadata.number === 0; // Session 0 is always unlocked
      let scheduledUnlockTime = null;

      // PRIORITY 1: Check manual override
      if (override) {
        const overrideStatus = (override.override_status || '').toLowerCase();
        isUnlocked = ['unlocked', 'complete', 'completed'].includes(
          overrideStatus,
        );
        console.log(
          `🔐 Practice Log Session ${metadata.number} has manual override: ${override.override_status}`,
        );
      } else if (metadata.number === 1) {
        // Session 1 unlocks after Session 0 completion OR (video_completed AND quiz_completed)
        const sessionZero = sessionsData.find((s) => s.session_number === 0);
        if (sessionZero) {
          isUnlocked =
            sessionZero.session_completed ||
            (sessionZero.video_completed && sessionZero.quiz_completed);
          console.log(
            `🔍 Practice Log Session 1 unlock check - Session 0 completed: ${sessionZero.session_completed}, video completed: ${sessionZero.video_completed}, quiz completed: ${sessionZero.quiz_completed}, unlocked: ${isUnlocked}`,
          );
        }
      } else if (metadata.number > 1) {
        // Sessions 2-5 unlock only after scheduled unlock time passes.
        if (schedule) {
          const scheduledUnlockTime =
            schedule[`session_${metadata.number}_unlock_time`];
          if (scheduledUnlockTime) {
            const now = new Date();
            const unlockTime = new Date(scheduledUnlockTime);
            const isScheduledUnlocked = now >= unlockTime;

            console.log(
              `📅 Practice Log Session ${metadata.number} scheduled check:`,
              {
                scheduledTime: unlockTime,
                currentTime: now,
                isScheduledUnlocked,
              },
            );

            if (!isScheduledUnlocked) {
              isUnlocked = false;
              const progress = {
                preAssessment: sessionData?.pre_assessment_completed || false,
                goalSetting:
                  sessionData?.weekly_goals !== null &&
                  sessionData?.weekly_goals !== undefined &&
                  sessionData?.weekly_goals !== '',
                video:
                  sessionData?.video_watched ||
                  sessionData?.video_completed ||
                  false,
                quiz: sessionData?.quiz_completed || false,
              };

              return {
                ...metadata,
                completed:
                  sessionData?.session_completed ||
                  sessionData?.session_fully_completed ||
                  false,
                unlocked: false,
                progress: progress,
                sessionStarted: sessionData?.session_started || false,
                scheduledUnlockTime: unlockTime,
              };
            }

            isUnlocked = true;
          } else {
            isUnlocked = false;
          }
        } else {
          isUnlocked = false;
        }
      }

      // Updated progress tracking with correct fields
      const progress = {
        preAssessment: sessionData?.pre_assessment_completed || false,
        goalSetting:
          sessionData?.weekly_goals !== null &&
          sessionData?.weekly_goals !== undefined &&
          sessionData?.weekly_goals !== '',
        video:
          sessionData?.video_watched || sessionData?.video_completed || false,
        quiz: sessionData?.quiz_completed || false,
        // postAssessment: sessionData?.post_assessment_completed || false
      };

      return {
        ...metadata,
        completed:
          sessionData?.session_completed ||
          sessionData?.session_fully_completed ||
          false,
        unlocked: isUnlocked,
        progress: progress,
        sessionStarted: sessionData?.session_started || false,
        scheduledUnlockTime: schedule?.[
          `session_${metadata.number}_unlock_time`
        ]
          ? new Date(schedule[`session_${metadata.number}_unlock_time`])
          : null,
      };
    });

    console.log(
      '✅ Practice Log: Sessions loaded with EXACT Dashboard logic:',
      transformedSessions,
    );
    setSessions(transformedSessions);
    setSessionsLoaded(true);
  } catch (error) {
    console.error('Error fetching session data:', error);
    setSessionsLoaded(true);
  }
}, [user]);

  // Session skills mapping
  const skillsBySession = {
    1: ['Deep Breathing'],
    2: ['Body Scan'],
    3: ['Catch, Check, and Change'],
    4: ['Acceptance', 'Goals for Re-engaging in Life'],
    5: ['Mindfulness of Pain and Discomfort'],
  };

  // Session homework requirements
  const sessionHomework = {
    1: {
      skills: [
        { name: 'Deep Breathing', required: 2, minMinutes: 5 }
      ],
      description: 'Deep Breathing: Practice twice for at least 5 minutes each'
    },
    2: {
      skills: [
        { name: 'Deep Breathing', required: 1, minMinutes: 5 },
        { name: 'Body Scan', required: 2, minMinutes: 5 }
      ],
      description: 'Deep Breathing: Once (5+ min) • Body Scan: Twice (5+ min each)'
    },
    3: {
      skills: [
        { name: 'Deep Breathing', required: 1, minMinutes: 5 },
        { name: 'Body Scan', required: 1, minMinutes: 5 },
        { name: 'Catch, Check, and Change', required: 2, minMinutes: 5 }
      ],
      description: 'Deep Breathing: Once (5+ min) • Body Scan: Once (5+ min) • Catch, Check, Change: Twice (5+ min each)'
    },
    4: {
      skills: [
        { name: 'Deep Breathing', required: 1, minMinutes: 5 },
        { name: 'Body Scan', required: 1, minMinutes: 5 },
        { name: 'Catch, Check, and Change', required: 1, minMinutes: 5 }
      ],
      goals: [
        'Acceptance: Work towards 2-3 or more goals before Session 5',
        'Goals for Re-engaging in Life: Work towards 2-3 or more goals before Session 5'
      ],
      description: 'Deep Breathing: Once (5+ min) • Body Scan: Once (5+ min) • Catch, Check, Change: Once (5+ min) • Goal Work: 2-3 goals for Acceptance & Re-engaging'
    },
    5: {
      skills: [],
      description: 'Continue practicing skills from previous sessions and maintain your goals'
    }
  };

  // Get current session number (highest unlocked session)
  const getCurrentSession = useCallback(() => {
    if (!sessionsLoaded || sessions.length === 0) return 1;
    
    // Find the highest unlocked session number
    let highestUnlocked = 1;
    sessions.forEach(session => {
      if (session.unlocked && session.number > 0) { // Skip session 0 (intro)
        highestUnlocked = Math.max(highestUnlocked, session.number);
      }
    });
    
    return highestUnlocked;
  }, [sessions, sessionsLoaded]);

  // Helper function to get the session week boundaries based on unlock schedule
  const getSessionWeekBoundaries = useCallback((sessionNumber) => {
    // Get the user's session schedule from the sessions state
    const session = sessions.find(s => s.number === sessionNumber);
    
    if (!session || !session.scheduledUnlockTime) {
      // Fallback to current week if no schedule available
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      
      return { startOfWeek, endOfWeek };
    }
    
    // Use the unlock time as the start of the week
    const startOfWeek = new Date(session.scheduledUnlockTime);
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return { startOfWeek, endOfWeek };
  }, [sessions]);

  // Helper function to get any 7-day period where user practiced daily
  // This checks if user has ANY 7 consecutive days of practice for a given session's skills
  const hasCompletedSevenDayStreak = useCallback((practiceEntries, sessionNumber) => {
    // Get all dates with practice, sorted chronologically
    const allDatesWithPractice = Object.keys(practiceEntries)
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => a - b);
    
    if (allDatesWithPractice.length < 7) return false;
    
    // Check for any 7 consecutive days
    for (let i = 0; i <= allDatesWithPractice.length - 7; i++) {
      let consecutiveDays = 1;
      
      for (let j = i; j < allDatesWithPractice.length - 1; j++) {
        const currentDate = allDatesWithPractice[j];
        const nextDate = allDatesWithPractice[j + 1];
        
        // Check if next date is exactly 1 day after current date
        const diffInDays = Math.round((nextDate - currentDate) / (1000 * 60 * 60 * 24));
        
        if (diffInDays === 1) {
          consecutiveDays++;
          if (consecutiveDays >= 7) {
            console.log(`✅ Found 7-day streak for Session ${sessionNumber} badge`);
            return true;
          }
        } else {
          break; // Streak broken
        }
      }
    }
    
    return false;
  }, []);

  // Get current active session week for badge tracking
  const getCurrentActiveSessionWeek = useCallback(() => {
    // Find the first unlocked session that isn't completed yet
    // This is the session the user should be actively working on
    for (let sessionNum = 1; sessionNum <= 5; sessionNum++) {
      const session = sessions.find(s => s.number === sessionNum);
      if (session?.unlocked && !session?.completed) {
        console.log(`🎯 Current active session for badge tracking: Session ${sessionNum}`);
        return sessionNum;
      }
    }
    
    // If all sessions are complete, return the highest session number
    console.log('🎯 All sessions complete, using getCurrentSession()');
    return getCurrentSession();
  }, [sessions, getCurrentSession]);

  // Show notification helper
  const showNotificationMessage = (message, type = 'success') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    
    setTimeout(() => {
      setShowNotification(false);
    }, 4000);
  };

  const resetPracticeForm = useCallback(() => {
    setEditingPracticeId(null);
    setSkill('');
    setMinutes('');
    setPracticeType('self-guided');
  }, []);

  const closePracticeModal = useCallback(() => {
    setIsModalOpen(false);
    resetPracticeForm();
  }, [resetPracticeForm]);

  // Calendar navigation functions
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(prev => prev - 1);
    } else {
      setCurrentMonth(prev => prev - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(prev => prev + 1);
    } else {
      setCurrentMonth(prev => prev + 1);
    }
  };

  const goToCurrentMonth = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  // Get current month name for display
  const getCurrentMonthName = useCallback(() => {
    const date = new Date(currentYear, currentMonth);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentMonth, currentYear]);

  // Generate calendar grid for specified month/year
  const generateCalendarGrid = useCallback(() => {    
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const prevMonth = new Date(currentYear, currentMonth - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    const totalCells = 42;
    const daysFromPrevMonth = startDayOfWeek;
    const daysFromNextMonth = totalCells - daysFromPrevMonth - daysInMonth;
    
    const calendarDays = [];
    
    // Previous month days
    for (let i = daysFromPrevMonth; i > 0; i--) {
      const day = daysInPrevMonth - i + 1;
      const date = new Date(currentYear, currentMonth - 1, day);
      calendarDays.push({
        day,
        date,
        isCurrentMonth: false,
        isToday: false,
        isPrevMonth: true
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      calendarDays.push({
        day,
        date,
        isCurrentMonth: true,
        isToday,
        isPrevMonth: false
      });
    }
    
    // Next month days
    for (let day = 1; day <= daysFromNextMonth; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      calendarDays.push({
        day,
        date,
        isCurrentMonth: false,
        isToday: false,
        isPrevMonth: false
      });
    }
    
    return calendarDays;
  }, [currentMonth, currentYear]);

  // Calculate homework progress
  const calculateHomeworkProgress = useCallback(() => {
    const currentSession = getCurrentSession();
    const homework = sessionHomework[currentSession];
    if (!homework || !homework.skills) return { completed: 0, total: 0, percentage: 0, totalMinutes: 0, skillProgress: [] };

    const skillProgress = homework.skills.map(skillReq => {
      const relevantPractices = Object.entries(practiceData).flatMap(([date, practicesData]) => {
        let practicesArray = Array.isArray(practicesData) ? practicesData : [practicesData];
        
        return practicesArray.filter(practice => {
          return practice.skill === skillReq.name && practice.minutes >= skillReq.minMinutes;
        });
      });

      const completed = Math.min(relevantPractices.length, skillReq.required);
      const isComplete = completed >= skillReq.required;

      return {
        skill: skillReq.name,
        completed,
        required: skillReq.required,
        isComplete,
        minMinutes: skillReq.minMinutes
      };
    });

    const totalCompleted = skillProgress.reduce((sum, skill) => sum + skill.completed, 0);
    const totalRequired = skillProgress.reduce((sum, skill) => sum + skill.required, 0);
    const percentage = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;
    const allSkillsComplete = skillProgress.every(skill => skill.isComplete);

    return {
      completed: totalCompleted,
      total: totalRequired,
      percentage,
      isComplete: allSkillsComplete,
      skillProgress,
      homework
    };
  }, [practiceData, getCurrentSession]);

  // Get homework feedback message
  const getHomeworkFeedback = useCallback(() => {
    const progress = calculateHomeworkProgress();
    const { completed, total, isComplete, totalMinutes, homework } = progress;
    const currentSession = getCurrentSession();
    
    if (isComplete) {
      return {
        message: `Perfect! You've completed all required practices for Session ${currentSession}. Great consistency!`,
        emoji: <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#FFFFFF">
          <path d="m80-80 200-560 360 360L80-80Zm132-132 282-100-182-182-100 282Zm370-246-42-42 224-224q32-32 77-32t77 32l24 24-42 42-24-24q-14-14-35-14t-35 14L582-458ZM422-618l-42-42 24-24q14-14 14-34t-14-34l-26-26 42-42 26 26q32 32 32 76t-32 76l-24 24Zm80 80-42-42 144-144q14-14 14-35t-14-35l-64-64 42-42 64 64q32 32 32 77t-32 77L502-538Zm160 160-42-42 64-64q32-32 77-32t77 32l64 64-42 42-64-64q-14-14-35-14t-35 14l-64 64ZM212-212Z"/>
        </svg>
      };
    } else if (completed > 0) {
      const remaining = total - completed;
      return {
        message: `Great progress! You've completed ${completed} of ${total} required practices. ${remaining} more to go!`,
        color: "#374151",
        emoji: <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#FFFFFF">
          <path d="M720-120H280v-520l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14Zm-360-80h360l120-280v-80H480l54-220-174 174v406Zm0-406v406-406Zm-80-34v80H160v360h120v80H80v-520h200Z"/>
        </svg>
      };
    } else {
      return {
        message: `Ready to start your Session ${currentSession} practice? Complete the required practices.`,
        color: "#374151",
        emoji: <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#FFFFFF">
          <path d="m480-120-58-52q-101-91-167-157T150-447.5Q111-500 95.5-544T80-634q0-94 63-157t157-63q52 0 99 22t81 62q34-40 81-62t99-22q94 0 157 63t63 157q0 46-15.5 90T810-447.5Q771-395 705-329T538-172l-58 52Zm0-108q96-86 158-147.5t98-107q36-45.5 50-81t14-70.5q0-60-40-100t-100-40q-47 0-87 26.5T518-680h-76q-15-41-55-67.5T300-774q-60 0-100 40t-40 100q0 35 14 70.5t50 81q36 45.5 98 107T480-228Zm0-273Z"/>
        </svg>
      };
    }
  }, [calculateHomeworkProgress, getCurrentSession]);

// Get available skills based on unlocked sessions
const getAvailableSkills = useCallback(() => {
  if (!sessionsLoaded) return [];

  const availableSkills = [];

  // Include skills only when the session is unlocked and its video is completed.
  sessions.forEach((session) => {
    const isSessionEligible = session.unlocked && session.progress?.video;

    if (isSessionEligible && session.number > 0) {
      const sessionSkills = skillsBySession[session.number] || [];
      availableSkills.push(...sessionSkills);
    }
  });

  // Remove duplicates and return
  return [...new Set(availableSkills)];
}, [sessions, sessionsLoaded]);

  // Calendar day content matching dashboard style
  const getCalendarDayContent = useCallback(({ date }) => {
    const dateStr = date.toDateString();
    const practicesForDate = practiceData[dateStr];
    
    if (!practicesForDate) return null;

    let practicesArray;
    if (Array.isArray(practicesForDate)) {
      practicesArray = practicesForDate;
    } else {
      practicesArray = [practicesForDate];
    }

    if (practicesArray.length === 0) return null;

    const totalMinutes = practicesArray.reduce((sum, practice) => sum + practice.minutes, 0);
    const primarySkill = practicesArray[0].skill;
    
    // Dashboard medical colors
    const colors = {
      1: '#1B365D',
      2: '#00A0B0', 
      3: '#4A90A4',
      4: '#7FCDCD',
      5: '#B8E6B8'
    };

    const sessionNumber = practicesArray[0].sessionNumber || 1;

    return {
      hasActivity: true,
      count: practicesArray.length,
      minutes: totalMinutes,
      color: colors[sessionNumber] || colors[1],
      skill: primarySkill
    };
  }, [practiceData]);

  // Get recent practice history with sorting and pagination
  const getRecentPracticeHistory = useCallback((page = 0, sortBy = 'date') => {
    const allPractices = [];
    
    Object.entries(practiceData).forEach(([dateStr, practicesData]) => {
      let practicesArray = Array.isArray(practicesData) ? practicesData : [practicesData];
      
      practicesArray.forEach(practice => {
        allPractices.push({
          ...practice,
          date: new Date(dateStr),
          dateStr: dateStr
        });
      });
    });
    
    // Sort based on selected option
    let sorted;
    switch (sortBy) {
      case 'session':
        sorted = allPractices.sort((a, b) => {
          if (a.sessionNumber !== b.sessionNumber) {
            return a.sessionNumber - b.sessionNumber;
          }
          return b.date - a.date;
        });
        break;
      case 'skill':
        sorted = allPractices.sort((a, b) => {
          if (a.skill !== b.skill) {
            return a.skill.localeCompare(b.skill);
          }
          return b.date - a.date;
        });
        break;
      case 'date':
      default:
        sorted = allPractices.sort((a, b) => b.date - a.date);
        break;
    }
    
    // Pagination
    const startIndex = page * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedResults = sorted.slice(startIndex, endIndex);
    
    return {
      practices: paginatedResults,
      totalPages: Math.ceil(sorted.length / ITEMS_PER_PAGE),
      currentPage: page,
      totalPractices: sorted.length,
      hasNext: endIndex < sorted.length,
      hasPrevious: page > 0
    };
  }, [practiceData]);

  // Get next badge goal
  const getNextBadgeGoal = useCallback(() => {
    const availableBadges = Object.entries(badgeDefinitions).filter(
      ([badgeName]) => !badges.includes(badgeName)
    );

    if (availableBadges.length === 0) {
      return {
        name: "Badge Master",
        description: "You've earned all available badges!",
        progress: 100,
        progressText: "Complete!",
        color: "#EFF6FF"
      };
    }

    // Prioritize showing the badge for the current active session (unlocked but not completed)
    const activeSessionNumber = getCurrentActiveSessionWeek();
    const activeSessionBadge = availableBadges.find(([_, badgeData]) => 
      badgeData.type === 'session_week' && badgeData.sessionNumber === activeSessionNumber
    );

    let closestBadge = null;
    let highestProgress = 0;

    // If there's an active session badge, prioritize it
    if (activeSessionBadge) {
      const [badgeName, badgeData] = activeSessionBadge;
      
      // Count current streak of consecutive days
      let currentStreak = 0;
      const today = new Date();
      const todayKey = today.toDateString();
      
      if (practiceData[todayKey]) {
        currentStreak = 1;
        let checkDate = new Date(today);
        
        for (let i = 1; i < 7; i++) {
          checkDate.setDate(checkDate.getDate() - 1);
          const dateStr = checkDate.toDateString();
          
          if (practiceData[dateStr]) {
            currentStreak++;
          } else {
            break;
          }
        }
      }
      
      const progress = Math.min((currentStreak / badgeData.requiredDays) * 100, 100);
      const remaining = Math.max(0, badgeData.requiredDays - currentStreak);
      
      closestBadge = {
        name: badgeName,
        description: badgeData.description,
        progress: Math.round(progress),
        progressText: remaining === 0 ? 'Complete - practice once more to earn!' : `Practice ${remaining} more consecutive days`,
        color: badgeData.color
      };
    } else {
      // Otherwise, find the closest badge to completion
      availableBadges.forEach(([badgeName, badgeData]) => {
        let progress = 0;
        let progressText = '';

        if (badgeData.type === 'session_week') {
          // Count current consecutive day streak
          let currentStreak = 0;
          const today = new Date();
          const todayKey = today.toDateString();
          
          if (practiceData[todayKey]) {
            currentStreak = 1;
            let checkDate = new Date(today);
            
            for (let i = 1; i < 7; i++) {
              checkDate.setDate(checkDate.getDate() - 1);
              const dateStr = checkDate.toDateString();
              
              if (practiceData[dateStr]) {
                currentStreak++;
              } else {
                break;
              }
            }
          }
          
          progress = Math.min((currentStreak / badgeData.requiredDays) * 100, 100);
          const remaining = Math.max(0, badgeData.requiredDays - currentStreak);
          progressText = remaining === 0 ? 'Complete - practice once more to earn!' : `Practice ${remaining} more consecutive days`;
        } else if (badgeData.type === 'milestone') {
          progress = Math.min((totalPractices / badgeData.requiredCount) * 100, 100);
          const remaining = Math.max(0, badgeData.requiredCount - totalPractices);
          progressText = remaining === 0 ? 'Complete!' : `${remaining} more total practices`;
        }

        if (progress > highestProgress || (progress === 100 && highestProgress < 100)) {
          highestProgress = progress;
          closestBadge = {
            name: badgeName,
            description: badgeData.description,
            progress: Math.round(progress),
            progressText,
            color: badgeData.color
          };
        }
      });
    }

    return closestBadge || {
      name: "Getting Started",
      description: "Begin your practice journey",
      progress: 0,
      progressText: "Start practicing to earn badges",
      color: "#EFF6FF"
    };
  }, [badges, practiceData, totalPractices, getCurrentActiveSessionWeek]);

  // Calculate weekly stats
  const calculateWeeklyStats = useCallback(async (practiceEntries) => {
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    weekStart.setHours(0, 0, 0, 0);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);
    weekEnd.setHours(0, 0, 0, 0);

    let totalMinutes = 0;
    let practiceCount = 0;
    const skillFrequency = {};
    const dailyPractice = {};
    
    Object.entries(practiceEntries).forEach(([dateStr, entry]) => {
      const practiceDate = new Date(dateStr);
      
      if (practiceDate >= weekStart && practiceDate < weekEnd) {
        let practices = Array.isArray(entry) ? entry : [entry];
        
        practices.forEach(practice => {
          totalMinutes += practice.minutes || 0;
          practiceCount++;
          skillFrequency[practice.skill] = (skillFrequency[practice.skill] || 0) + 1;
        });
        
        const dayOfWeek = practiceDate.getDay();
        dailyPractice[dayOfWeek] = true;
      }
    });

    const completionPercentage = Math.round((Object.keys(dailyPractice).length / 7) * 100);
    
    let topSkill = '';
    let topSkillCount = 0;
    Object.entries(skillFrequency).forEach(([skill, count]) => {
      if (count > topSkillCount) {
        topSkill = skill;
        topSkillCount = count;
      }
    });

    setWeeklyStats({
      totalMinutes,
      practiceCount,
      topSkill,
      completionPercentage,
      dailyPractice
    });
  }, []);

  const rebuildPracticeState = useCallback((practiceEntries) => {
    const practiceDataMap = {};
    const skillCountMap = {};
    let totalCount = 0;

    practiceEntries.forEach((entry) => {
      const [year, month, day] = entry.practice_date.split('-');
      const practiceDate = new Date(year, month - 1, day);
      const dateKey = practiceDate.toDateString();

      if (!practiceDataMap[dateKey]) {
        practiceDataMap[dateKey] = [];
      }

      practiceDataMap[dateKey].push({
        id: entry.id,
        skill: entry.skill,
        minutes: entry.minutes,
        practiceType: entry.practice_type,
        sessionNumber: entry.session_number,
        timestamp: entry.timestamp
      });

      skillCountMap[entry.skill] = (skillCountMap[entry.skill] || 0) + 1;
      totalCount++;
    });

    let streak = 0;
    const today = new Date();
    const todayKey = today.toDateString();

    if (practiceDataMap[todayKey]) {
      streak = 1;
      let currentDate = new Date(today);

      for (let i = 1; i <= 30; i++) {
        currentDate.setDate(currentDate.getDate() - 1);
        const dateStr = currentDate.toDateString();

        if (practiceDataMap[dateStr]) {
          streak++;
        } else {
          break;
        }
      }
    }

    setPracticeData(practiceDataMap);
    setSkillCounts(skillCountMap);
    setTotalPractices(totalCount);
    setCurrentStreak(streak);
    calculateWeeklyStats(practiceDataMap);
  }, [calculateWeeklyStats]);

  const openNewPracticeModal = useCallback((date) => {
    setSelectedDate(date);
    resetPracticeForm();
    setIsModalOpen(true);
  }, [resetPracticeForm]);

  const openEditPracticeModal = useCallback((practice) => {
    setSelectedDate(new Date(practice.date));
    setEditingPracticeId(practice.id);
    setSkill(practice.skill || '');
    setMinutes(practice.minutes ? String(practice.minutes) : '');
    setPracticeType(practice.practiceType || 'self-guided');
    setIsModalOpen(true);
  }, []);

  const handleDeletePracticeEntry = useCallback(async (practice) => {
    if (!practice?.id || !user?.id) return;

    const formattedDate = practice.date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    const confirmDelete = window.confirm(
      `Delete this practice log?\n\n${practice.skill} • ${practice.minutes} minutes • ${formattedDate}`
    );

    if (!confirmDelete) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('practice_logs')
        .delete()
        .eq('id', practice.id)
        .eq('user_id', user.id);

      if (error) throw error;

      const refreshedEntries = await getUserPracticeLog(user.id);
      rebuildPracticeState(refreshedEntries);

      if (editingPracticeId === practice.id) {
        closePracticeModal();
      }

      setHistoryPage(0);
      showNotificationMessage('Practice log deleted successfully.', 'success');
    } catch (error) {
      console.error('Error deleting practice log:', error);
      showNotificationMessage('Failed to delete practice log. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }, [user, rebuildPracticeState, editingPracticeId, closePracticeModal]);

  // Handle calendar day click
  const handleDayClick = useCallback((date, e) => {
    e.stopPropagation();
    
    const today = new Date();
    if (date > today) return;

    openNewPracticeModal(date);
  }, [openNewPracticeModal]);

  // Enhanced form submission
  const handleSubmit = useCallback(async () => {
    if (!selectedDate || !skill || !minutes || !practiceType) {
      showNotificationMessage("Please fill in all fields", 'error');
      return;
    }

    const minutesNum = parseInt(minutes);
    if (isNaN(minutesNum) || minutesNum <= 0 || minutesNum > 180) {
      showNotificationMessage("Please enter a valid duration between 1 and 180 minutes", 'error');
      return;
    }

    if (!user?.id) {
      showNotificationMessage("User not authenticated. Please log in again.", 'error');
      return;
    }

    setSaving(true);

    // Determine session number based on skill
    let sessionNumber = 1; // Default
    sessions.forEach(session => {
      const isSessionEligible =
        session.unlocked && (session.progress?.video || session.completed);

      if (isSessionEligible && session.number > 0) {
        const sessionSkills = skillsBySession[session.number] || [];
        if (sessionSkills.includes(skill)) {
          sessionNumber = session.number;
        }
      }
    });

    const practiceLogData = {
      date: selectedDate.toISOString().split('T')[0],
      skill,
      minutes: minutesNum,
      practiceType,
      sessionNumber
    };

    try {
      if (editingPracticeId) {
        const { error } = await supabase
          .from('practice_logs')
          .update({
            practice_date: practiceLogData.date,
            skill: practiceLogData.skill,
            minutes: practiceLogData.minutes,
            practice_type: practiceLogData.practiceType,
            session_number: practiceLogData.sessionNumber,
            timestamp: Date.now()
          })
          .eq('id', editingPracticeId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        await savePracticeLog(user.id, practiceLogData);
      }

      const refreshedEntries = await getUserPracticeLog(user.id);
      rebuildPracticeState(refreshedEntries);
      await checkForNewBadges(refreshedEntries, refreshedEntries.length);

      closePracticeModal();
      showNotificationMessage(editingPracticeId ? "Practice log updated successfully!" : "Practice logged successfully!", 'success');

    } catch (error) {
      console.error("Error saving practice log:", error);
      showNotificationMessage(editingPracticeId ? "Failed to update practice. Please try again." : "Failed to save practice. Please try again.", 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedDate, skill, minutes, practiceType, user, sessions, editingPracticeId, rebuildPracticeState, closePracticeModal]);

  // Badge checking
  const checkForNewBadges = useCallback(async (practiceEntries, totalPractices) => {
    if (!user?.id) return;

    const newBadgesToAward = [];

    Object.entries(badgeDefinitions).forEach(([badgeName, badgeData]) => {
      if (badges.includes(badgeName)) return;

      let shouldAward = false;
      
      if (badgeData.type === 'session_week') {
        // Award badge if user has ANY 7 consecutive days of practice (ever)
        shouldAward = hasCompletedSevenDayStreak(practiceEntries, badgeData.sessionNumber);
      } else if (badgeData.type === 'milestone') {
        // Check total practice count
        shouldAward = totalPractices >= badgeData.requiredCount;
      }

      if (shouldAward) {
        newBadgesToAward.push(badgeName);
      }
    });

    if (newBadgesToAward.length > 0) {
      try {
        for (const badgeName of newBadgesToAward) {
          await awardBadge(user.id, badgeName);
        }

        setBadges(prev => [...prev, ...newBadgesToAward]);
        setNewBadge(newBadgesToAward[0]);
        setShowBadgeDialog(true);
      } catch (error) {
        console.error("Error awarding badges:", error);
      }
    }
  }, [user, badges, hasCompletedSevenDayStreak]);

  // Fetch badges from Supabase
  const fetchSupabaseBadges = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      const userBadges = await fetchUserBadges(userId);
      setBadges(userBadges);
      setBadgesLoaded(true);
    } catch (error) {
      console.error("Error fetching badges:", error);
      setBadgesLoaded(true);
    }
  }, []);

  // Modern badge rendering with dashboard colors
  const renderBadge = (badgeName) => {
    const badgeInfo = badgeDefinitions[badgeName];
    if (!badgeInfo) return null;

    return (
      <div
        key={badgeName}
        style={{
          backgroundColor: badgeInfo.color,
          borderRadius: '16px',
          padding: '8px 12px',
          margin: '4px',
          fontSize: '12px',
          fontWeight: '600',
          color: '#374151',
          border: '1px solid #E5E7EB',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}
        title={badgeInfo.description}
      >
        <svg xmlns="http://www.w3.org/2000/svg" height="12px" viewBox="0 -960 960 960" width="12px" fill="#374151">
          <path d="M280-120v-80h160v-124q-49-11-87.5-41.5T296-442q-75-9-125.5-65.5T120-640v-40q0-33 23.5-56.5T200-760h80v-80h400v80h80q33 0 56.5 23.5T840-680v40q0 76-50.5 132.5T664-442q-18 46-56.5 76.5T520-324v124h160v80H280Zm0-408v-152h-80v40q0 38 22 68.5t58 43.5Zm200 128q50 0 85-35t35-85v-240H360v240q0 50 35 85t85 35Zm200-128q36-13 58-43.5t22-68.5v-40h-80v152Zm-200-52Z"/>
        </svg>
        {badgeName}
      </div>
    );
  };

  // Load user data - updated to include session fetching
  useEffect(() => {
    if (user?.id) {
      fetchSupabaseBadges(user.id);
      fetchUserSessions();
    } else {
      setBadges([]);
      setBadgesLoaded(false);
      setSessions([]);
      setSessionsLoaded(false);
    }
  }, [user, fetchSupabaseBadges, fetchUserSessions]);

  // Fetch practice data - updated to wait for both badges and sessions
  useEffect(() => {
    const fetchPracticeData = async () => {
      if (!user?.id || !badgesLoaded || !sessionsLoaded) {
        return;
      }

      try {
        setLoading(true);
        
        const practiceEntries = await getUserPracticeLog(user.id);
        
        rebuildPracticeState(practiceEntries);

      } catch (error) {
        console.error("Error fetching practice data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPracticeData();
  }, [user, badgesLoaded, sessionsLoaded, rebuildPracticeState]);

  // Navigation to guided practices
  const navigateToGuidedPractices = () => {
    window.location.href = '/guided-practices';
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        backgroundColor: '#F8F9FA',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <MainLayout currentSessions={sessions} currentPath={location.pathname}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f1f3f4',
                borderTop: '3px solid #1B365D',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <p style={{ 
                color: '#374151', 
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '16px',
                fontWeight: '400'
              }}>
                Loading your practice journey...
              </p>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#F8F9FA',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <MainLayout currentSessions={sessions} currentPath={location.pathname}>
        <div style={{
          flex: 1,
          width: '100%',
          padding: isMobile ? '16px 20px' : '32px 40px',
          overflow: 'auto',
          paddingBottom: isMobile ? '80px' : '32px',
          ...(isMobile && {
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          })
        }}>
          {/* Back Button - Desktop Only */}
          {!isMobile && (
            <div style={{
              paddingTop: '32px',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.7';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#374151">
                  <path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z"/>
                </svg>
                Back to Dashboard
              </button>
            </div>
          )}

          {/* Header - Updated to match dashboard mobile header */}
          <div style={{
            padding: isMobile ? '0' : '0',
            backgroundColor: '#F8F9FA',
            marginBottom: '32px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              marginBottom: isMobile ? '0' : '16px',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '0' : '8px',
              paddingTop: isMobile ? '24px' : '0'
            }}>
              {isMobile ? (
                // Mobile Layout - Logo and Practice Log title side-by-side
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  width: '100%'
                }}>
                  {/* Logo Section */}
                  <div style={{
                    width: 'calc(40% - 8px)',
                    height: '82px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    flexShrink: 0
                  }}>
                    <img 
                      src="/assets/FFWLOGO.png"
                      alt="Face Forward Web Logo"
                      style={{
                        width: '80%',
                        filter: 'brightness(0) saturate(100%) invert(11%) sepia(66%) saturate(1347%) hue-rotate(202deg) brightness(93%) contrast(107%)',
                        height: '60%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* SVG fallback */}
                    <svg width="40" height="25" fill="#1B365D" viewBox="0 0 24 24" style={{ display: 'none' }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>

                  {/* Practice Log Title - Takes remaining width */}
                  <div style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '20px 24px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    height: '82px'
                  }}>
                    <h1 style={{
                      fontSize: '28px',
                      fontWeight: '600',
                      color: '#1B365D',
                      margin: 0,
                      letterSpacing: '-1px',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      textAlign: 'center'
                    }}>
                      Practice Log
                    </h1>
                  </div>
                </div>
              ) : (
                // Desktop Layout
                <h1 style={{
                  fontSize: '48px',
                  fontWeight: '600',
                  color: '#1B365D',
                  margin: 0,
                  letterSpacing: '-1px',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  Home Practice Log
                </h1>
              )}
            </div>
            {/* Desktop subtitle */}
            {!isMobile && (
              <p style={{
                fontSize: '16px',
                fontWeight: '400',
                color: '#6B7280',
                margin: '0',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Track sessions, monitor progress & earn badges
              </p>
            )}
          </div>

          {/* Main Content */}
          <div style={{
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : '2fr 1fr',
            gap: '32px'
          }}>
            {/* Mobile Order: Right Column First (Homework, Badge, Achievements) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              order: isMobile ? 1 : 2
            }}>
              {/* Homework Assignment Card */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                padding: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#1B365D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontWeight: '700',
                    fontSize: '16px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    border: '1px solid #E5E7EB'
                  }}>
                    {getCurrentSession()}
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 4px 0',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      Session {getCurrentSession()} Practice
                    </h3>
                  </div>
                </div>

                {/* Progress Visualization */}
                <div style={{
                  backgroundColor: '#EFF6FF',
                  borderRadius: '8px',
                  border: '1px solid #3B82F6',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Progress
                    </span>
                    <div style={{
                      backgroundColor: calculateHomeworkProgress().isComplete ? '#ECFDF5' : '#1B365D',
                      color: calculateHomeworkProgress().isComplete ? '#374151' : '#FFFFFF',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      border: '1px solid #E5E7EB'
                    }}>
                      {calculateHomeworkProgress().percentage}%
                    </div>
                  </div>

                  {/* Individual Skill Progress Sections */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    {calculateHomeworkProgress().skillProgress.map((skillProgress, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: skillProgress.isComplete ? '#ECFDF5' : '#FFFFFF',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1
                        }}>
                          {/* Multiple checkboxes for required count */}
                          <div style={{
                            display: 'flex',
                            gap: '4px'
                          }}>
                            {Array.from({ length: skillProgress.required }).map((_, checkIndex) => (
                              <div key={checkIndex} style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: checkIndex < skillProgress.completed ? '#1B365D' : '#e5e7eb',
                                border: '1px solid #E5E7EB',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px',
                                color: checkIndex < skillProgress.completed ? '#FFFFFF' : '#9ca3af',
                                fontWeight: 'bold',
                                flexShrink: 0
                              }}>
                                {checkIndex < skillProgress.completed && '✓'}
                              </div>
                            ))}
                          </div>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#374151'
                          }}>
                            {skillProgress.skill}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '11px',
                          color: skillProgress.isComplete ? '#374151' : '#6b7280',
                          fontWeight: '500'
                        }}>
                          {skillProgress.completed}/{skillProgress.required} ({skillProgress.minMinutes}+ min each)
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Overall Progress Summary */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: '#374151',
                      fontWeight: '500'
                    }}>
                      Overall Progress: {calculateHomeworkProgress().completed} of {calculateHomeworkProgress().total} practices
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${calculateHomeworkProgress().percentage}%`,
                      height: '100%',
                      backgroundColor: '#374151',
                      borderRadius: '3px',
                      transition: 'width 0.8s ease'
                    }} />
                  </div>
                </div>

                {/* Feedback Message */}
                <div style={{
                  backgroundColor: calculateHomeworkProgress().isComplete ? '#1B365D' : '#F3F4F6',
                  borderRadius: '8px',
                  padding: '12px',
                  border: '1px solid #E5E7EB'
                }}>
                  <p style={{
                    fontSize: '13px',
                    color: calculateHomeworkProgress().isComplete ? '#FFFFFF' : '#374151',
                    lineHeight: '1.4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    margin: 0
                  }}>
                    <span style={{ fontSize: '16px' }}>{getHomeworkFeedback().emoji}</span>
                    {getHomeworkFeedback().message}
                  </p>
                </div>
              </div>

              {/* NEW: Guided Videos Card */}
              <div style={{
                backgroundColor: '#1B365D',
                borderRadius: '16px',
                padding: '24px',
                color: '#FFFFFF',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
              onClick={navigateToGuidedPractices}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="m10 16.5 6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    </svg>
                  </div>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    Guided Videos
                  </h4>
                </div>
                <p style={{
                  fontSize: '14px',
                  margin: '0 0 16px 0',
                  opacity: 0.9,
                  lineHeight: '1.4'
                }}>
                  Watch step-by-step practice demonstrations
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  <span>{getAvailableSkills().length} videos available</span>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
                  </svg>
                </div>
              </div>

              {/* Calendar Card - Mobile Only */}
              {isMobile && (
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: isMobile ? '16px' : '24px',
                  minHeight: isMobile ? '420px' : '450px',
                  position: 'relative',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  ...(isMobile && {
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  })
                }}>
                  {/* Interactive Calendar section - MOBILE RESPONSIVE */}
                  <div style={{
                    backgroundColor: '#F8F9FA',
                    borderRadius: '12px',
                    padding: isMobile ? '24px' : '32px',
                    marginBottom: '16px',
                    height: isMobile ? '400px' : '420px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #E5E7EB',
                    flexShrink: 0,
                    ...(isMobile && {
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflowX: 'hidden'
                    })
                  }}>
                    {/* Interactive calendar grid - MOBILE RESPONSIVE */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      width: isMobile ? '100%' : '360px',
                      maxWidth: isMobile ? '380px' : '360px',
                      margin: '0 auto'
                    }}>
                      {/* Month navigation header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        marginBottom: '12px'
                      }}>
                        <button
                          onClick={goToPreviousMonth}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#374151',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(107, 114, 128, 0.1)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          ‹
                        </button>
                        
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {getCurrentMonthName()}
                        </span>
                        
                        <button
                          onClick={goToNextMonth}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#374151',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease',
                            width: '40px',
                            height: '40px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(107, 114, 128, 0.1)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          ›
                        </button>
                      </div>
                      
                      {/* Calendar grid - MOBILE RESPONSIVE */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: isMobile ? '4px' : '6px',
                        width: '100%',
                        maxWidth: '100%'
                      }}>
                        {/* Calendar header */}
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                          <div key={i} style={{
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: '600',
                            textAlign: 'center',
                            color: '#6B7280',
                            marginBottom: '8px',
                            height: isMobile ? '20px' : '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {day}
                          </div>
                        ))}
                        
                        {/* Calendar days with real dates - MOBILE RESPONSIVE */}
                        {generateCalendarGrid().map((calendarDay, i) => {
                          const { day, date, isCurrentMonth, isToday, isPrevMonth } = calendarDay;
                          const dayContent = getCalendarDayContent({ date });
                          const isFuture = date > new Date();
                          
                          return (
                            <div key={i} style={{
                              width: isMobile ? '40px' : '44px',
                              height: isMobile ? '40px' : '44px',
                              fontSize: isMobile ? '14px' : '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: dayContent?.hasActivity ? '#EFF6FF' : (isToday ? '#1B365D' : 'transparent'),
                              color: isCurrentMonth ? 
                                (dayContent?.hasActivity ? '#1B365D' : (isToday ? '#FFFFFF' : '#374151')) : 
                                '#D1D5DB',
                              border: dayContent?.hasActivity ? '1px solid #3B82F6' : '1px solid transparent',
                              borderRadius: '6px',
                              position: 'relative',
                              transition: 'all 0.2s ease',
                              cursor: (isFuture || !isCurrentMonth) ? 'not-allowed' : 'pointer',
                              fontWeight: isCurrentMonth ? '500' : '300',
                              opacity: isCurrentMonth ? 1 : 0.5,
                              ...(isMobile && {
                                minWidth: '40px',
                                maxWidth: '40px',
                                boxSizing: 'border-box'
                              })
                            }}
                            title={dayContent?.hasActivity ? 
                              `${dayContent.skill} - ${dayContent.minutes} minutes` : 
                              (isToday ? 'Today - Click to log practice' : (isFuture ? 'Future date' : 'Click to log practice'))
                            }
                            onClick={(e) => isCurrentMonth && !isFuture && handleDayClick(date, e)}
                            onMouseEnter={(e) => {
                              if (isCurrentMonth && !isFuture) {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.zIndex = '10';
                                if (!dayContent?.hasActivity && !isToday) {
                                  e.target.style.backgroundColor = '#F0F9FF';
                                }
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (isCurrentMonth && !isFuture) {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.zIndex = '1';
                                if (!dayContent?.hasActivity && !isToday) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }
                            }}
                          >
                            {day}
                            {/* Activity indicator dot - REMOVED ON MOBILE */}
                            {!isMobile && dayContent?.hasActivity && dayContent.count > 1 && (
                              <div style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                border: `1px solid #3B82F6`,
                                fontSize: '9px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#1B365D',
                                fontWeight: 'bold'
                              }}>
                                {dayContent.count}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Button at bottom */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                  }}>
                    <button style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1B365D',
                      border: '1px solid #3B82F6',
                      borderRadius: '20px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const today = new Date();
                      openNewPracticeModal(today);
                    }}>
                      Log Today's Practice
                    </button>
                  </div>
                </div>
              )}

              {/* Next Badge Goal - Desktop and Mobile */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                padding: '24px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 20px 0',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  Next Badge Goal
                </h3>
                
                {(() => {
                  const nextBadge = getNextBadgeGoal();
                  return (
                    <div>
                      <div style={{
                        backgroundColor: nextBadge.color,
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '16px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#374151">
                            <path d="M280-120v-80h160v-124q-49-11-87.5-41.5T296-442q-75-9-125.5-65.5T120-640v-40q0-33 23.5-56.5T200-760h80v-80h400v80h80q33 0 56.5 23.5T840-680v40q0 76-50.5 132.5T664-442q-18 46-56.5 76.5T520-324v124h160v80H280Zm0-408v-152h-80v40q0 38 22 68.5t58 43.5Zm200 128q50 0 85-35t35-85v-240H360v240q0 50 35 85t85 35Zm200-128q36-13 58-43.5t22-68.5v-40h-80v152Zm-200-52Z"/>
                          </svg>
                          <div>
                            <h4 style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 4px 0'
                            }}>
                              {nextBadge.name}
                            </h4>
                            <p style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              margin: 0
                            }}>
                              {nextBadge.description}
                            </p>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            color: '#6B7280',
                            fontWeight: '500'
                          }}>
                            Progress
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: '#374151',
                            fontWeight: '600'
                          }}>
                            {nextBadge.progress}%
                          </span>
                        </div>
                        
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: 'rgba(107, 114, 128, 0.1)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${nextBadge.progress}%`,
                            height: '100%',
                            backgroundColor: '#374151',
                            borderRadius: '4px',
                            transition: 'width 0.8s ease'
                          }} />
                        </div>
                      </div>
                      
                      <div style={{
                        textAlign: 'center',
                        padding: '12px',
                        backgroundColor: '#F8F9FA',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <span style={{
                          fontSize: '14px',
                          color: '#374151',
                          fontWeight: '500'
                        }}>
                          {nextBadge.progressText}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Badges */}
              {badges.length > 0 && (
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  padding: '24px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 20px 0',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    Achievements
                  </h3>
                  
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {badges.map(badgeName => renderBadge(badgeName))}
                  </div>
                </div>
              )}
            </div>

            {/* Desktop Calendar - Left Column */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                order: 1
              }}>
                {/* Calendar Card */}
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '24px',
                  minHeight: '450px',
                  position: 'relative',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Interactive Calendar section */}
                  <div style={{
                    backgroundColor: '#F8F9FA',
                    borderRadius: '12px',
                    padding: '32px',
                    marginBottom: '16px',
                    height: '550px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #E5E7EB',
                    flexShrink: 0
                  }}>
                    {/* Interactive calendar grid */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      width: '100%',
                      maxWidth: '100%',
                      margin: '0'
                    }}>
                      {/* Month navigation header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        marginBottom: '12px'
                      }}>
                        <button
                          onClick={goToPreviousMonth}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#374151',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px',
                            position: 'relative',
                            zIndex: 1000,
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(107, 114, 128, 0.1)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          ‹
                        </button>
                        
                        <button
                          onClick={goToCurrentMonth}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            fontWeight: '600',
                            textAlign: 'center',
                            color: '#374151',
                            cursor: 'pointer',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(107, 114, 128, 0.1)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          {getCurrentMonthName()}
                        </button>
                        
                        <button
                          onClick={goToNextMonth}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#374151',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease',
                            width: '40px',
                            height: '40px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(107, 114, 128, 0.1)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          ›
                        </button>
                      </div>
                      
                      {/* Calendar grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '6px',
                        width: '100%',
                        maxWidth: '100%'
                      }}>
                        {/* Calendar header */}
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                          <div key={i} style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            textAlign: 'center',
                            color: '#6B7280',
                            marginBottom: '8px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {day}
                          </div>
                        ))}
                        
                        {/* Calendar days with real dates */}
                        {generateCalendarGrid().map((calendarDay, i) => {
                          const { day, date, isCurrentMonth, isToday, isPrevMonth } = calendarDay;
                          const dayContent = getCalendarDayContent({ date });
                          const isFuture = date > new Date();
                          
                          return (
                            <div key={i} style={{
                              width: '44px',
                              height: '44px',
                              fontSize: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: dayContent?.hasActivity ? '#EFF6FF' : (isToday ? '#1B365D' : 'transparent'),
                              color: isCurrentMonth ? 
                                (dayContent?.hasActivity ? '#1B365D' : (isToday ? '#FFFFFF' : '#374151')) : 
                                '#D1D5DB',
                              border: dayContent?.hasActivity ? '1px solid #3B82F6' : '1px solid transparent',
                              borderRadius: '6px',
                              position: 'relative',
                              transition: 'all 0.2s ease',
                              cursor: (isFuture || !isCurrentMonth) ? 'not-allowed' : 'pointer',
                              fontWeight: isCurrentMonth ? '500' : '300',
                              opacity: isCurrentMonth ? 1 : 0.5
                            }}
                            title={dayContent?.hasActivity ? 
                              `${dayContent.skill} - ${dayContent.minutes} minutes` : 
                              (isToday ? 'Today - Click to log practice' : (isFuture ? 'Future date' : 'Click to log practice'))
                            }
                            onClick={(e) => isCurrentMonth && !isFuture && handleDayClick(date, e)}
                            onMouseEnter={(e) => {
                              if (isCurrentMonth && !isFuture) {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.zIndex = '10';
                                if (!dayContent?.hasActivity && !isToday) {
                                  e.target.style.backgroundColor = '#F0F9FF';
                                }
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (isCurrentMonth && !isFuture) {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.zIndex = '1';
                                if (!dayContent?.hasActivity && !isToday) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }
                            }}
                          >
                            {day}
                            {/* Activity indicator dot */}
                            {dayContent?.hasActivity && dayContent.count > 1 && (
                              <div style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                border: `1px solid #3B82F6`,
                                fontSize: '9px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#1B365D',
                                fontWeight: 'bold'
                              }}>
                                {dayContent.count}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Title and button at bottom */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {/* Header row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '500',
                        color: '#374151',
                        margin: 0
                      }}>
                      </h3>
                      <button style={{
                        backgroundColor: '#EFF6FF',
                        color: '#1B365D',
                        border: '1px solid #3B82F6',
                        borderRadius: '20px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        const today = new Date();
                        openNewPracticeModal(today);
                      }}>
                        Log Today's Practice
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recent Practice History - Desktop Only */}
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  padding: '24px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: recentActivityCollapsed ? '0' : '20px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setRecentActivityCollapsed(!recentActivityCollapsed)}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0,
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#374151">
                        <path d="M480-120q-138 0-240.5-91.5T122-440h82q14 104 92.5 172T480-200q117 0 198.5-81.5T760-480q0-117-81.5-198.5T480-760q-69 0-129 32t-101 88h110v80H120v-240h80v94q51-64 124.5-99T480-840q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-480q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-120Zm112-192L440-464v-216h80v184l128 128-56 56Z"/>
                      </svg>
                      Recent Activity
                    </h3>
                    
                    {/* Collapse/Expand Button */}
                    <button style={{
                      backgroundColor: '#F8F9FA',
                      color: '#374151',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      {recentActivityCollapsed ? 'Show' : 'Hide'}
                      <span style={{ fontSize: '10px' }}>
                        {recentActivityCollapsed ? '▼' : '▲'}
                      </span>
                    </button>
                  </div>
                  
                  {!recentActivityCollapsed && (
                    (() => {
                      const historyData = getRecentPracticeHistory(historyPage, 'date'); // Always use date sort
                      
                      return historyData.totalPractices > 0 ? (
                        <div>
                          {/* Practice entries */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            marginBottom: historyData.totalPages > 1 ? '20px' : '0'
                          }}>
                            {historyData.practices.map((practice, index) => {
                              const colors = {
                                1: '#EFF6FF',
                                2: '#F0F9FF', 
                                3: '#F8F9FA',
                                4: '#ECFDF5',
                                5: '#F3F4F6'
                              };
                              
                              const sessionColor = colors[practice.sessionNumber] || colors[1];
                              
                              return (
                                <div key={practice.id || `${practice.dateStr}-${practice.skill}-${index}`} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '16px',
                                  padding: '16px',
                                  backgroundColor: sessionColor,
                                  borderRadius: '12px',
                                  border: '1px solid #E5E7EB',
                                  transition: 'transform 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
                                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                                >
                                  {/* Date circle */}
                                  <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E5E7EB',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}>
                                    <div style={{
                                      fontSize: '16px',
                                      fontWeight: '700',
                                      color: '#374151',
                                      lineHeight: '1'
                                    }}>
                                      {practice.date.getDate()}
                                    </div>
                                    <div style={{
                                      fontSize: '8px',
                                      color: '#6b7280',
                                      fontWeight: '600',
                                      textTransform: 'uppercase',
                                      lineHeight: '1'
                                    }}>
                                      {practice.date.toLocaleDateString('en-US', { month: 'short' })}
                                    </div>
                                  </div>
                                  
                                  {/* Practice details */}
                                  <div style={{ flex: 1 }}>
                                    <div style={{
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      color: '#374151',
                                      marginBottom: '4px'
                                    }}>
                                      {practice.skill}
                                    </div>
                                   <div style={{
                                      fontSize: '13px',
                                      color: '#6B7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}>
                                      <span>{practice.minutes} minutes</span>
                                      <span>•</span>
                                      <span>{practice.practiceType === 'self-guided' ? 'On my own' : 'With video'}</span>
                                    </div>
                                  </div>

                                  {practice.id && (
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '6px',
                                      flexShrink: 0
                                    }}>
                                      <button
                                        onClick={() => openEditPracticeModal(practice)}
                                        style={{
                                          backgroundColor: '#FFFFFF',
                                          color: '#1B365D',
                                          border: '1px solid #3B82F6',
                                          borderRadius: '16px',
                                          padding: '4px 10px',
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeletePracticeEntry(practice)}
                                        style={{
                                          backgroundColor: '#FFFFFF',
                                          color: '#b42318',
                                          border: '1px solid #fda29b',
                                          borderRadius: '16px',
                                          padding: '4px 10px',
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Session badge */}
                                  <div style={{
                                    backgroundColor: '#FFFFFF',
                                    color: '#374151',
                                    borderRadius: '16px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    border: '1px solid #E5E7EB',
                                    flexShrink: 0
                                  }}>
                                    S{practice.sessionNumber}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Pagination controls */}
                          {historyData.totalPages > 1 && (
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '12px 0'
                            }}>
                              <button
                                onClick={() => setHistoryPage(prev => Math.max(0, prev - 1))}
                                disabled={!historyData.hasPrevious}
                                style={{
                                  background: 'none',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '20px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: historyData.hasPrevious ? 'pointer' : 'not-allowed',
                                  color: historyData.hasPrevious ? '#374151' : '#9ca3af',
                                  backgroundColor: historyData.hasPrevious ? '#FFFFFF' : '#f5f5f5',
                                  fontFamily: 'system-ui, -apple-system, sans-serif',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                ‹ Previous
                              </button>
                              
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: '500'
                              }}>
                                {historyData.currentPage + 1} of {historyData.totalPages}
                              </div>
                              
                              <button
                                onClick={() => setHistoryPage(prev => prev + 1)}
                                disabled={!historyData.hasNext}
                                style={{
                                  background: 'none',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '20px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: historyData.hasNext ? 'pointer' : 'not-allowed',
                                  color: historyData.hasNext ? '#374151' : '#9ca3af',
                                  backgroundColor: historyData.hasNext ? '#FFFFFF' : '#f5f5f5',
                                  fontFamily: 'system-ui, -apple-system, sans-serif',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                Next ›
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{
                          textAlign: 'center',
                          padding: '32px 20px',
                          backgroundColor: '#F8F9FA',
                          borderRadius: '12px',
                          border: '1px solid #E5E7EB'
                        }}>
                          <div style={{
                            fontSize: '40px',
                            marginBottom: '12px',
                            opacity: 0.5
                          }}>
                            📝
                          </div>
                          <p style={{
                            fontSize: '14px',
                            margin: '0 0 4px 0',
                            color: '#6b7280',
                            fontWeight: '500'
                          }}>
                            No practice sessions logged yet
                          </p>
                          <p style={{
                            fontSize: '12px',
                            color: '#9ca3af',
                            margin: 0
                          }}>
                            Start logging your practices to see your history here
                          </p>
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '24px 24px 0 24px',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: 0,
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    {editingPracticeId ? 'Edit Practice Session' : 'Log Practice Session'}
                  </h3>
                  <button
                    onClick={closePracticeModal}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '4px'
                    }}
                  >
                    ×
                  </button>
                </div>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '0 0 16px 0'
                }}>
                  {selectedDate?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '24px' }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  {/* Date */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={selectedDate ? new Date(selectedDate.getTime() - (selectedDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0] : ''}
                      max={new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString().split('T')[0]}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const [year, month, day] = e.target.value.split('-').map(Number);
                        setSelectedDate(new Date(year, month - 1, day));
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        fontSize: '14px',
                        backgroundColor: '#FFFFFF',
                        color: '#374151'
                      }}
                    />
                  </div>

                  {/* Skill Selection */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      What did you practice?
                    </label>
                    <select
                      value={skill}
                      onChange={(e) => setSkill(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        fontSize: '14px',
                        backgroundColor: '#FFFFFF',
                        color: '#374151'
                      }}
                    >
                      <option value="">Select a practice...</option>
                      {getAvailableSkills().map(skillOption => (
                        <option key={skillOption} value={skillOption}>
                          {skillOption}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Duration */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={minutes}
                      onChange={(e) => setMinutes(e.target.value)}
                      placeholder="Enter minutes (e.g., 15)"
                      min="1"
                      max="180"
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        fontSize: '14px',
                        backgroundColor: '#FFFFFF',
                        color: '#374151'
                      }}
                    />
                  </div>

                  {/* Practice Type */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Practice Type
                    </label>
                    <select
                      value={practiceType}
                      onChange={(e) => setPracticeType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        fontSize: '14px',
                        backgroundColor: '#FFFFFF',
                        color: '#374151'
                      }}
                    >
                      <option value="self-guided">Practiced on my own (without video)</option>
                      <option value="rewatched-video">Practiced with video</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      borderRadius: '24px',
                      border: '1px solid #E5E7EB',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      backgroundColor: saving ? '#f5f5f5' : '#1B365D',
                      color: saving ? '#9ca3af' : '#FFFFFF',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {saving ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #9ca3af',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        {editingPracticeId ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      editingPracticeId ? 'Update Practice Log' : 'Log Practice Session'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Badge Dialog */}
        {showBadgeDialog && newBadge && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              padding: '32px',
              textAlign: 'center',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '16px'
              }}>
                🏆
              </div>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 8px 0',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Achievement Unlocked!
              </h3>
              <div style={{
                backgroundColor: badgeDefinitions[newBadge]?.color || '#EFF6FF',
                borderRadius: '16px',
                padding: '16px',
                margin: '16px 0',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#374151" style={{marginRight: '6px'}}>
                    <path d="M280-120v-80h160v-124q-49-11-87.5-41.5T296-442q-75-9-125.5-65.5T120-640v-40q0-33 23.5-56.5T200-760h80v-80h400v80h80q33 0 56.5 23.5T840-680v40q0 76-50.5 132.5T664-442q-18 46-56.5 76.5T520-324v124h160v80H280Zm0-408v-152h-80v40q0 38 22 68.5t58 43.5Zm200 128q50 0 85-35t35-85v-240H360v240q0 50 35 85t85 35Zm200-128q36-13 58-43.5t22-68.5v-40h-80v152Zm-200-52Z"/>
                  </svg>
                  {newBadge}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  {badgeDefinitions[newBadge]?.description}
                </div>
              </div>
              <button
                onClick={() => setShowBadgeDialog(false)}
                style={{
                  backgroundColor: '#1B365D',
                  color: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Notification */}
        {showNotification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: notificationType === 'success' ? '#ECFDF5' : '#FEF2F2',
            color: '#374151',
            padding: '16px 20px',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            zIndex: 1002,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                {notificationMessage}
              </span>
              <button
                onClick={() => setShowNotification(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  marginLeft: '12px'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </MainLayout>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Mobile-specific styles */
          @media (max-width: 768px) {
            * {
              box-sizing: border-box;
            }
            
            body {
              overflow-x: hidden;
              margin: 0;
              padding: 0;
            }
            
            /* Force all containers to stay within viewport */
            .dashboard-container, 
            .dashboard-container * {
              max-width: 100vw !important;
              box-sizing: border-box !important;
            }
            
            /* Prevent any element from extending beyond screen */
            div, section, article, main {
              max-width: 100% !important;
              overflow-x: hidden !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default ModernPracticeLog;
